import { Link } from "react-router-dom";
import SectionHeading from "@/components/base/SectionHeading";

export interface CarePanel {
  title: string;
  text: string;
  image: string;
  imageAlt: string;
  /** Optional internal link; when provided the panel shows a "Learn more" action. */
  to?: string;
}

interface CarePanelsProps {
  eyebrow: string;
  title: string;
  description?: string;
  panels: CarePanel[];
  /** Default destination for panels that do not define their own. */
  defaultTo?: string;
  tone?: "light" | "tinted";
}

/**
 * Large horizontal visual panels used for the specialist care areas.
 * Image and text alternate sides down the list for an editorial rhythm.
 */
export default function CarePanels({
  eyebrow,
  title,
  description,
  panels,
  defaultTo,
  tone = "tinted",
}: CarePanelsProps) {
  const background = tone === "tinted" ? "bg-background-100" : "bg-background-50";

  return (
    <section className={`${background} py-16 md:py-24`}>
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeading eyebrow={eyebrow} title={title} description={description} />

        <div className="mt-12 space-y-6">
          {panels.map((panel, index) => {
            const reverse = index % 2 === 1;
            const href = panel.to ?? defaultTo;
            const card = (
              <div className="grid grid-cols-1 overflow-hidden rounded-2xl border border-background-200 bg-background-50 md:grid-cols-12">
                <div
                  className={`relative h-56 w-full overflow-hidden md:col-span-5 md:h-full ${
                    reverse ? "md:order-2" : "md:order-1"
                  }`}
                >
                  <img
                    src={panel.image}
                    alt={panel.imageAlt}
                    title={panel.imageAlt}
                    className="h-full w-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div
                    className="absolute inset-0 bg-gradient-to-t from-primary-950/30 to-transparent md:bg-gradient-to-r"
                    aria-hidden="true"
                  ></div>
                </div>

                <div className={`flex flex-col justify-center p-6 md:col-span-7 md:p-9 ${reverse ? "md:order-1" : "md:order-2"}`}>
                  <span className="font-label text-xs font-semibold uppercase tracking-[0.18em] text-accent-700">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-3 font-heading text-2xl font-semibold text-foreground-950">{panel.title}</h3>
                  <p className="mt-3 max-w-2xl font-body text-sm leading-relaxed text-foreground-600 md:text-base">
                    {panel.text}
                  </p>
                  {href ? (
                    <span className="mt-5 inline-flex items-center gap-2 font-label text-sm font-semibold text-primary-700">
                      Learn more
                      <i
                        className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"
                        aria-hidden="true"
                      ></i>
                    </span>
                  ) : null}
                </div>
              </div>
            );

            return href ? (
              <Link key={panel.title} to={href} className="reveal group block cursor-pointer">
                {card}
              </Link>
            ) : (
              <div key={panel.title} className="reveal group">
                {card}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}